import { divideRounded } from '@fd/money';
import { addDays, addMonths, daysBetween, daysInMonth, startOfMonth, weekday, type IsoDate } from './dates.ts';
import type { AccountType, TransactionKind, TransactionStatus } from './types.ts';
import { nextWorkingDay, previousWorkingDay, ukBankHolidays } from './workdays.ts';

// Recurring payments detected from booked transactions (spec §50–51): subscriptions, bills,
// debt repayments, salaries and standing transfers. Pure and deterministic, so the device and a
// server job reach the same result. Nothing below the confidence threshold is ever reported —
// a guess shown as "recurring" is worse than a pattern found a month later.

export type Cadence = 'WEEKLY' | 'FORTNIGHTLY' | 'FOUR_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export type RecurringKind = 'SUBSCRIPTION' | 'BILL' | 'DEBT' | 'INCOME' | 'TRANSFER' | 'OTHER';

export type RecurringTransaction = {
  readonly id: string;
  readonly accountId: string;
  readonly bookingDate: IsoDate;
  /** Signed from the account's perspective: outflow negative. */
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly kind: TransactionKind;
  readonly status: TransactionStatus;
  readonly merchant: string | null;
  readonly description: string | null;
  readonly categoryId: string | null;
  readonly counterpartAccountId: string | null;
};

export type PriceChange = { readonly previousMinor: bigint; readonly currentMinor: bigint; readonly since: IsoDate };

export type RecurringPattern = {
  /** Stable identity of the pattern (same input → same signature on every device). */
  readonly signature: string;
  readonly merchantKey: string;
  /** Merchant or description as the bank last wrote it. */
  readonly name: string;
  readonly accountId: string;
  readonly counterpartAccountId: string | null;
  readonly currency: string;
  readonly direction: 'IN' | 'OUT';
  readonly cadence: Cadence;
  readonly kind: RecurringKind;
  /** Positive amounts. Typical = latest for a fixed price, median of recent ones otherwise. */
  readonly typicalAmountMinor: bigint;
  readonly minAmountMinor: bigint;
  readonly maxAmountMinor: bigint;
  readonly lastAmountMinor: bigint;
  readonly fixedAmount: boolean;
  readonly priceChange: PriceChange | null;
  /** Usual day of the month for monthly, quarterly and yearly patterns. */
  readonly dayOfMonth: number | null;
  readonly firstDate: IsoDate;
  readonly lastDate: IsoDate;
  /** When it should reach the bank: the due date moved off weekends and bank holidays. */
  readonly nextDate: IsoDate;
  /** The due date by the calendar alone (the 29th, every Friday). */
  readonly dueDate: IsoDate;
  /** The expected date has passed (beyond the usual slack) without a payment. */
  readonly overdue: boolean;
  /** False when several cycles were missed: most likely cancelled or paid off. */
  readonly active: boolean;
  readonly transactionIds: readonly string[];
  /** Every payment in the pattern, oldest first (positive amounts). */
  readonly occurrences: readonly { readonly id: string; readonly date: IsoDate; readonly amountMinor: bigint }[];
  readonly categoryId: string | null;
  /** 0–10 000 (basis points). */
  readonly confidence: number;
  /** Cost normalised to a month and to a year (positive). */
  readonly monthlyMinor: bigint;
  readonly yearlyMinor: bigint;
};

export type DetectRecurringOptions = {
  readonly today: IsoDate;
  /** Account types, so a transfer into a loan or card account counts as a debt repayment. */
  readonly accountTypes?: ReadonlyMap<string, AccountType>;
  /** Patterns below this confidence (basis points) are not reported. Default 6 000. */
  readonly minConfidence?: number;
};

type CadenceRule = {
  readonly cadence: Cadence;
  /** Accepted gap between two payments, in days. */
  readonly min: number;
  readonly max: number;
  readonly nominalDays: number;
  readonly minCount: number;
  /** Monthly equivalent = amount × perYear / 12. */
  readonly perYear: bigint;
};

const RULES: readonly CadenceRule[] = [
  { cadence: 'WEEKLY', min: 6, max: 8, nominalDays: 7, minCount: 4, perYear: 52n },
  { cadence: 'FORTNIGHTLY', min: 12, max: 16, nominalDays: 14, minCount: 3, perYear: 26n },
  { cadence: 'FOUR_WEEKLY', min: 27, max: 29, nominalDays: 28, minCount: 3, perYear: 13n },
  { cadence: 'MONTHLY', min: 25, max: 35, nominalDays: 30, minCount: 3, perYear: 12n },
  { cadence: 'QUARTERLY', min: 80, max: 101, nominalDays: 91, minCount: 3, perYear: 4n },
  { cadence: 'YEARLY', min: 350, max: 380, nominalDays: 365, minCount: 2, perYear: 1n },
];

// ------------------------------------------------------------------------------ merchant keys

/** Card processors whose own name comes first: the merchant is after the asterisk. */
const PROCESSORS = new Set(['paypal', 'pp', 'sq', 'sumup', 'zettle', 'izettle', 'stripe', 'google', 'gpay', 'payu', 'przelewy24', 'p24']);
const PREFIXES =
  /^(card payment to|card payment|contactless payment to|direct debit to|direct debit|dd to|standing order to|standing order|so to|faster payment to|faster payment|bill payment to|bill payment|payment to|payment from|transfer to|transfer from|platnosc karta|platnosc blik|platnosc|przelew do|przelew na|przelew z|przelew|zlecenie stale|polecenie zaplaty)\s+/;
const NOISE = new Set([
  'ltd', 'limited', 'plc', 'llc', 'inc', 'gmbh', 'sa', 'sp', 'z', 'o', 'oo', 'uk', 'gb', 'the', 'ddr', 'dd', 'so', 'bgc',
  'fpo', 'fpi', 'pos', 'vis', 'ref', 'on', 'via', 'card', 'payment', 'www', 'com',
  // "TESCO STORES 3321" and "Tesco" are the same shop.
  'stores', 'store', 'smkts', 'supermarket', 'supermarkets',
]);

/**
 * A merchant's identity without the bank's noise: prefixes ("CARD PAYMENT TO"), references,
 * dates, card numbers, legal suffixes and web domains. "CARD PAYMENT TO SPOTIFY P3A7B2 ON 12/03"
 * and "Spotify" both become "spotify".
 */
export function merchantKey(text: string): string {
  let s = text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/ł/g, 'l').trim();
  // Cash from a LINK machine ("LNK ASDA BURY 1 S"): the shop is only where the machine stands,
  // so every withdrawal is one "merchant", never the shop.
  if (/^lnk\b/.test(s)) return 'lnk';
  const star = s.indexOf('*');
  if (star >= 0) {
    const before = s.slice(0, star).trim();
    s = PROCESSORS.has(before) && s.slice(star + 1).trim() ? s.slice(star + 1) : before || s.slice(star + 1);
  }
  for (let previous = ''; previous !== s; ) {
    previous = s;
    s = s.replace(PREFIXES, '').trim();
  }
  s = s.replace(/\b(www\.)/g, '').replace(/\.(com|co\.uk|org\.uk|uk|pl|net|org|io|de|eu|tv)\b/g, '');
  const tokens = s
    .split(/[^a-z0-9]+/)
    .filter((token) => token !== '' && !NOISE.has(token))
    // References, dates and card numbers carry two or more digits ("o2" and "3" stay).
    .filter((token) => (token.match(/\d/g)?.length ?? 0) < 2);
  return tokens.slice(0, 3).join(' ');
}

const SUBSCRIPTIONS = [
  'netflix', 'spotify', 'disney', 'prime video', 'amazon prime', 'amzn prime', 'apple', 'itunes', 'icloud', 'youtube',
  'google one', 'google storage', 'microsoft', 'xbox', 'playstation', 'psn', 'nintendo', 'adobe', 'dropbox', 'openai',
  'chatgpt', 'anthropic', 'claude', 'audible', 'kindle', 'deezer', 'tidal', 'now tv', 'nowtv', 'paramount', 'dazn',
  'hbo', 'player', 'patreon', 'github', 'notion', '1password', 'nordvpn', 'expressvpn', 'canva', 'duolingo', 'strava',
  'puregym', 'gym', 'virgin active', 'david lloyd', 'nuffield', 'anytime fitness', 'fitness', 'multisport',
];
const BILLS = [
  'rent', 'czynsz', 'landlord', 'letting', 'lettings', 'council tax', 'council', 'water', 'thames water', 'severn trent',
  'british gas', 'octopus', 'edf', 'eon', 'e on', 'ovo', 'scottish power', 'sse', 'tauron', 'enea', 'energa', 'pge',
  'pgnig', 'ee', 'vodafone', 'o2', 'three', 'giffgaff', 'bt', 'sky', 'virgin media', 'talktalk', 'plusnet',
  'hyperoptic', 'orange', 't mobile', 'tmobile', 'upc', 'vectra', 'netia', 'insurance', 'ubezpieczenie', 'aviva',
  'admiral', 'direct line', 'axa', 'pzu', 'warta', 'allianz', 'hestia', 'tv licence', 'tv licensing', 'nursery',
  'electric', 'energy', 'broadband',
  // Water in the North West (and utilities in general) do not say "water".
  'united utilities', 'utilities', 'utility', 'dwr cymru',
];
const DEBTS = [
  'loan', 'loans', 'finance', 'kredyt', 'rata', 'raty', 'leasing', 'mortgage', 'hipoteka', 'pozyczka', 'black horse',
  'motonovo', 'close brothers', 'santander consumer', 'vwfs', 'volkswagen financial', 'bmw financial',
  'toyota financial', 'ford credit', 'hitachi capital', 'zopa', 'klarna', 'paypal credit', 'car finance', 'pcp',
  'hire purchase',
];
/**
 * Filling the tank: a regular cost, but one the fuel gauge sets, not the calendar — fills come 2 to 6
 * weeks apart and the amounts vary (the owner counts "Car Fuel" among the bills).
 */
const FUEL = ['petrol', 'fuel', 'pfs', 'paliwo', 'stacja paliw'];
// Paying off a credit card is not a cost: the purchases on the card already count as spending.
// A repayment into a tracked card account is an own transfer; an untracked card bill stays OTHER.

const hasPhrase = (key: string, phrases: readonly string[]) => phrases.some((phrase) => ` ${key} `.includes(` ${phrase} `));

// ------------------------------------------------------------------------------ detection

type Series = {
  readonly key: string;
  readonly accountId: string;
  readonly currency: string;
  readonly direction: 'IN' | 'OUT';
  readonly counterpartAccountId: string | null;
  readonly items: readonly RecurringTransaction[];
};

const magnitude = (value: bigint) => (value < 0n ? -value : value);

function median(values: readonly bigint[]): bigint {
  const sorted = [...values].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle]! : divideRounded(sorted[middle - 1]! + sorted[middle]!, 2n, 'HALF_EVEN');
}

function medianNumber(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

/** Which transactions can form a pattern, and in which direction they are counted. */
function direction(transaction: RecurringTransaction): 'IN' | 'OUT' | null {
  if (transaction.status !== 'BOOKED') return null;
  if (transaction.kind === 'EXPENSE' && transaction.amountMinor < 0n) return 'OUT';
  if (transaction.kind === 'INCOME' && transaction.amountMinor > 0n) return 'IN';
  // An own transfer is counted once, on the side the money leaves.
  if (transaction.kind === 'TRANSFER' && transaction.amountMinor < 0n) return 'OUT';
  return null;
}

function groupSeries(transactions: readonly RecurringTransaction[]): Series[] {
  const groups = new Map<string, { accountId: string; currency: string; direction: 'IN' | 'OUT'; key: string; counterpart: string | null; items: RecurringTransaction[] }>();
  for (const transaction of transactions) {
    const dir = direction(transaction);
    if (!dir) continue;
    const key =
      transaction.kind === 'TRANSFER' && transaction.counterpartAccountId
        ? `account:${transaction.counterpartAccountId}`
        : merchantKey(transaction.merchant ?? transaction.description ?? '');
    if (key === '') continue;
    const id = `${transaction.accountId}|${dir}|${transaction.currency}|${key}`;
    let group = groups.get(id);
    if (!group) {
      group = { accountId: transaction.accountId, currency: transaction.currency, direction: dir, key, counterpart: transaction.counterpartAccountId, items: [] };
      groups.set(id, group);
    }
    group.items.push(transaction);
  }
  return [...groups.values()].map((g) => ({
    key: g.key,
    accountId: g.accountId,
    currency: g.currency,
    direction: g.direction,
    counterpartAccountId: g.counterpart,
    items: [...g.items].sort((a, b) => (a.bookingDate !== b.bookingDate ? (a.bookingDate < b.bookingDate ? -1 : 1) : a.id < b.id ? -1 : 1)),
  }));
}

/** Clusters payments of the same merchant by amount (±2 %), largest cluster first. */
function amountClusters(items: readonly RecurringTransaction[]): RecurringTransaction[][] {
  const sorted = [...items].sort((a, b) => {
    const x = magnitude(a.amountMinor);
    const y = magnitude(b.amountMinor);
    return x < y ? -1 : x > y ? 1 : a.id < b.id ? -1 : 1;
  });
  const clusters: RecurringTransaction[][] = [];
  for (const item of sorted) {
    const current = clusters.at(-1);
    const anchor = current ? magnitude(current[0]!.amountMinor) : 0n;
    if (current && magnitude(item.amountMinor) * 100n <= anchor * 102n) current.push(item);
    else clusters.push([item]);
  }
  return clusters
    .map((cluster) => cluster.sort((a, b) => (a.bookingDate !== b.bookingDate ? (a.bookingDate < b.bookingDate ? -1 : 1) : a.id < b.id ? -1 : 1)))
    .sort((a, b) => b.length - a.length);
}

/** Share of gaps that fit the cadence; a single skipped cycle counts as half a fit. */
function regularity(gaps: readonly number[], rule: CadenceRule): number {
  if (gaps.length === 0) return 0;
  let score = 0;
  for (const gap of gaps) {
    if (gap >= rule.min && gap <= rule.max) score += 1;
    else if (gap >= rule.min * 2 && gap <= rule.max * 2) score += 0.5;
  }
  return score / gaps.length;
}

function pickRule(gaps: readonly number[]): CadenceRule | null {
  if (gaps.length === 0) return null;
  const typical = medianNumber(gaps);
  // Four-weekly only when every gap is exactly four weeks give or take a day; otherwise monthly.
  const fourWeekly = RULES.find((r) => r.cadence === 'FOUR_WEEKLY')!;
  if (gaps.length >= 2 && gaps.every((g) => g >= fourWeekly.min && g <= fourWeekly.max)) {
    const spread = Math.max(...gaps) - Math.min(...gaps);
    if (spread <= 1 && typical <= 28.5) return fourWeekly;
  }
  return RULES.find((r) => r.cadence !== 'FOUR_WEEKLY' && typical >= r.min && typical <= r.max) ?? null;
}

function monthlyAnchored(rule: CadenceRule): boolean {
  return rule.cadence === 'MONTHLY' || rule.cadence === 'QUARTERLY' || rule.cadence === 'YEARLY';
}

/** Next expected date: for calendar cadences, snapped to the usual day of the month. */
function nextDate(last: IsoDate, rule: CadenceRule, dayOfMonth: number | null): IsoDate {
  if (!monthlyAnchored(rule) || dayOfMonth === null) return addDays(last, rule.nominalDays);
  const months = rule.cadence === 'MONTHLY' ? 1 : rule.cadence === 'QUARTERLY' ? 3 : 12;
  const estimate = addMonths(last, months);
  // Paid a day early (rent due on the 1st, paid on the 31st) still points at the next due date.
  const candidates = [-1, 0, 1].map((shift) => {
    const month = addMonths(`${estimate.slice(0, 8)}01`, shift);
    const [y, m] = month.split('-').map(Number) as [number, number];
    return `${month.slice(0, 8)}${String(Math.min(dayOfMonth, daysInMonth(y, m))).padStart(2, '0')}`;
  });
  return candidates.reduce((best, candidate) =>
    Math.abs(daysBetween(estimate, candidate)) < Math.abs(daysBetween(estimate, best)) ? candidate : best,
  );
}

function countScore(count: number, rule: CadenceRule): number {
  if (rule.cadence === 'YEARLY') return count >= 4 ? 9500 : count === 3 ? 8000 : 6000;
  if (rule.cadence === 'QUARTERLY') return count >= 6 ? 9500 : count >= 4 ? 8000 : 7000;
  if (count >= 12) return 10_000;
  if (count >= 9) return 9300;
  if (count >= 6) return 8500;
  if (count >= 5) return 7800;
  if (count >= 4) return 7000;
  return 6000;
}

/** Price history of a fixed-price service: one old price, then one new price. */
function priceChangeOf(items: readonly RecurringTransaction[]): PriceChange | null {
  const amounts = items.map((t) => magnitude(t.amountMinor));
  const current = amounts.at(-1)!;
  let start = amounts.length - 1;
  while (start > 0 && amounts[start - 1] === current) start--;
  if (start === 0) return null;
  const previous = amounts[start - 1]!;
  if (!amounts.slice(0, start).every((a) => a === previous)) return null;
  return { previousMinor: previous, currentMinor: current, since: items[start]!.bookingDate };
}

function evaluate(series: Series, items: readonly RecurringTransaction[], options: DetectRecurringOptions, clustered: boolean): RecurringPattern | null {
  if (items.length < 2) return null;
  // Two payments on one day are one purchase split in two, a duplicate or two lines of one service:
  // the rhythm counts that day once. More than one such day in five is a habit, not a commitment.
  const dates = [...new Set(items.map((t) => t.bookingDate))];
  if ((items.length - dates.length) * 5 > items.length) return null;
  const gaps = dates.slice(1).map((date, i) => daysBetween(dates[i]!, date));
  const rule = pickRule(gaps);
  if (!rule || dates.length < rule.minCount) return null;
  const regular = regularity(gaps, rule);
  // A tank filled about monthly passes with half of its gaps a month long, once there are six fills.
  const fuel = series.direction === 'OUT' && hasPhrase(series.key, FUEL);
  if (regular < (fuel && rule.cadence === 'MONTHLY' && dates.length >= 6 ? 0.5 : 0.75)) return null;

  const recent = items.slice(-6).map((t) => magnitude(t.amountMinor));
  const all = items.map((t) => magnitude(t.amountMinor));
  const last = all.at(-1)!;
  const priceChange = priceChangeOf(items);
  const fixedAmount = recent.every((a) => a === last) || priceChange !== null;
  const typical = fixedAmount ? last : median(recent);
  const min = recent.reduce((a, b) => (b < a ? b : a));
  const max = recent.reduce((a, b) => (b > a ? b : a));
  const spread = typical === 0n ? 1 : Number(max - min) / Number(typical);
  const amountScore = fixedAmount ? 10_000 : spread <= 0.1 ? 9000 : spread <= 0.25 ? 7500 : spread <= 0.5 ? 5500 : 3000;

  const accountType = series.counterpartAccountId ? options.accountTypes?.get(series.counterpartAccountId) : undefined;
  const key = series.key;
  const nameKey = merchantKey(items.at(-1)!.merchant ?? items.at(-1)!.description ?? '');
  const kind: RecurringKind =
    series.direction === 'IN'
      ? 'INCOME'
      : accountType === 'LOAN' || accountType === 'MORTGAGE' || accountType === 'LIABILITY'
        ? 'DEBT'
        : items[0]!.kind === 'TRANSFER'
          ? 'TRANSFER'
          : hasPhrase(key, DEBTS)
            ? 'DEBT'
            : hasPhrase(key, SUBSCRIPTIONS)
              ? 'SUBSCRIPTION'
              : hasPhrase(key, BILLS) || fuel
                ? 'BILL'
                : 'OTHER';
  const known = hasPhrase(key, SUBSCRIPTIONS) || hasPhrase(key, BILLS) || hasPhrase(key, DEBTS) || hasPhrase(nameKey, DEBTS) || fuel;
  // Two equal payments a year apart at a shop or a cash machine are chance ("Asda Superstore
  // yearly"); an unknown yearly payment out waits for its third year.
  if (rule.cadence === 'YEARLY' && series.direction === 'OUT' && items.length < 3 && !known) return null;

  let confidence = Math.round(0.45 * regular * 10_000 + 0.3 * countScore(dates.length, rule) + 0.25 * amountScore) + (known ? 500 : 0);
  // Wildly different amounts at a steady rhythm (weekly shopping) are a habit, not a commitment.
  if (amountScore <= 3000 && !known) confidence = Math.min(confidence, 5500);
  confidence = Math.min(10_000, confidence);
  if (confidence < (options.minConfidence ?? 6000)) return null;

  const days = items.map((t) => Number(t.bookingDate.slice(8, 10)));
  const dayOfMonth = monthlyAnchored(rule) ? Math.round(medianNumber(days)) : null;
  const due = nextDate(items.at(-1)!.bookingDate, rule, dayOfMonth);
  const next = onBankDay(due, series.direction);
  const slack = Math.max(3, Math.round((rule.max - rule.min) / 2) + 1);
  const late = daysBetween(next, options.today);
  const monthlyMinor = divideRounded(typical * rule.perYear, 12n, 'HALF_EVEN');
  const counts = new Map<string | null, number>();
  for (const t of items) counts.set(t.categoryId, (counts.get(t.categoryId) ?? 0) + 1);
  const categoryId = [...counts.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))[0]![0];

  return {
    signature: `${series.accountId}|${series.direction}|${series.currency}|${key}|${rule.cadence}${clustered ? `|${typical}` : ''}`,
    merchantKey: key,
    name: (items.at(-1)!.merchant ?? items.at(-1)!.description ?? key).trim(),
    accountId: series.accountId,
    counterpartAccountId: series.counterpartAccountId,
    currency: series.currency,
    direction: series.direction,
    cadence: rule.cadence,
    kind,
    typicalAmountMinor: typical,
    minAmountMinor: min,
    maxAmountMinor: max,
    lastAmountMinor: last,
    fixedAmount,
    priceChange,
    dayOfMonth,
    firstDate: items[0]!.bookingDate,
    lastDate: items.at(-1)!.bookingDate,
    nextDate: next,
    dueDate: due,
    overdue: late > slack,
    active: late <= rule.nominalDays * 2,
    transactionIds: items.map((t) => t.id),
    occurrences: items.map((t) => ({ id: t.id, date: t.bookingDate, amountMinor: magnitude(t.amountMinor) })),
    categoryId,
    confidence,
    monthlyMinor,
    yearlyMinor: typical * rule.perYear,
  };
}

/**
 * Finds recurring payments. A merchant's payments are first tried as one series (a bill whose
 * amount changes every month); if they are not regular, each price point is tried on its own
 * (Amazon Prime among random Amazon orders).
 */
export function detectRecurring(transactions: readonly RecurringTransaction[], options: DetectRecurringOptions): RecurringPattern[] {
  const patterns: RecurringPattern[] = [];
  for (const series of groupSeries(transactions)) {
    const whole = evaluate(series, series.items, options, false);
    if (whole) {
      patterns.push(whole);
      continue;
    }
    for (const cluster of amountClusters(series.items)) {
      const found = evaluate(series, cluster, options, true);
      if (found) patterns.push(found);
    }
  }
  return patterns.sort((a, b) =>
    a.nextDate !== b.nextDate ? (a.nextDate < b.nextDate ? -1 : 1) : a.signature < b.signature ? -1 : a.signature > b.signature ? 1 : 0,
  );
}

/**
 * The day the money moves when the due date is not a working day in England and Wales: a payment
 * out (direct debit, standing order, card renewal) on the next working day, pay in on the last
 * working day before — how UK payrolls pay.
 */
export function onBankDay(date: IsoDate, direction: 'IN' | 'OUT'): IsoDate {
  return direction === 'OUT' ? nextWorkingDay(date) : previousWorkingDay(date);
}

/**
 * A payment the bank took on another day than its due date, because the due date was not a working
 * day: the car loan due on Saturday 29 August 2026 left on Tuesday 1 September (Monday was the
 * summer bank holiday) — a payment for August in September's list. `reason` names what was skipped.
 */
export type BankDayShift = { readonly dueDate: IsoDate; readonly reason: 'WEEKEND' | 'HOLIDAY' | 'BOTH' };

export function bankDayShift(pattern: Pick<RecurringPattern, 'cadence' | 'dayOfMonth' | 'direction'>, date: IsoDate): BankDayShift | null {
  if (!pattern.dayOfMonth || pattern.cadence === 'WEEKLY' || pattern.cadence === 'FORTNIGHTLY' || pattern.cadence === 'FOUR_WEEKLY') return null;
  // Its own month first: a payment on its due day was not moved at all.
  for (const offset of [0, -1, 1]) {
    const month = addMonths(startOfMonth(date), offset);
    const day = Math.min(pattern.dayOfMonth, daysInMonth(Number(month.slice(0, 4)), Number(month.slice(5, 7))));
    const due = `${month.slice(0, 8)}${String(day).padStart(2, '0')}`;
    if (due === date) return null;
    if (onBankDay(due, pattern.direction) !== date) continue;
    const skipped: IsoDate[] = [];
    for (let d = due; d !== date; d = addDays(d, pattern.direction === 'OUT' ? 1 : -1)) skipped.push(d);
    const holiday = skipped.some((d) => ukBankHolidays(Number(d.slice(0, 4))).has(d));
    const weekend = skipped.some((d) => weekday(d) >= 6);
    return { dueDate: due, reason: holiday && weekend ? 'BOTH' : holiday ? 'HOLIDAY' : 'WEEKEND' };
  }
  return null;
}

/** Every occurrence through `until`, anchored to the calendar so short months/holidays do not drift. */
export function expectedDates(
  pattern: Pick<RecurringPattern, 'nextDate' | 'cadence'> & Partial<Pick<RecurringPattern, 'dueDate' | 'direction' | 'dayOfMonth'>>,
  until: IsoDate,
): IsoDate[] {
  const step = pattern.cadence === 'WEEKLY' ? 7 : pattern.cadence === 'FORTNIGHTLY' ? 14 : pattern.cadence === 'FOUR_WEEKLY' ? 28 : null;
  const dates: IsoDate[] = [];
  if (pattern.nextDate <= until) dates.push(pattern.nextDate);
  const months = pattern.cadence === 'MONTHLY' ? 1 : pattern.cadence === 'QUARTERLY' ? 3 : 12;
  const anchor = pattern.dueDate ?? pattern.nextDate;
  for (let i = 1; ; i++) {
    let due = step ? addDays(anchor, step * i) : addMonths(anchor, months * i);
    if (!step && pattern.dayOfMonth) {
      const day = Math.min(pattern.dayOfMonth, daysInMonth(Number(due.slice(0, 4)), Number(due.slice(5, 7))));
      due = `${due.slice(0, 8)}${String(day).padStart(2, '0')}`;
    }
    const date = pattern.direction ? onBankDay(due, pattern.direction) : due;
    if (date > until) break;
    dates.push(date);
  }
  return dates;
}

/**
 * What a pattern actually brought in (or cost) per month over the last full calendar months —
 * pay that differs week to week, not the typical amount times the rhythm. Only months the
 * pattern covers whole count (the month it started in does not). Null before one full month.
 */
export function recentMonthlyAverage(pattern: Pick<RecurringPattern, 'occurrences' | 'firstDate'>, today: IsoDate, months = 3): bigint | null {
  const thisMonth = startOfMonth(today);
  const firstWhole = addMonths(startOfMonth(pattern.firstDate), 1);
  const wanted = addMonths(thisMonth, -months);
  const start = firstWhole > wanted ? firstWhole : wanted;
  const count = (Number(thisMonth.slice(0, 4)) - Number(start.slice(0, 4))) * 12 + Number(thisMonth.slice(5, 7)) - Number(start.slice(5, 7));
  if (count < 1) return null;
  const sum = pattern.occurrences.filter((o) => o.date >= start && o.date < thisMonth).reduce((total, o) => total + o.amountMinor, 0n);
  return divideRounded(sum, BigInt(count), 'HALF_EVEN');
}

